import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  SunIcon, 
  MoonIcon, 
  Bars3Icon, 
  XMarkIcon, 
  ArrowUpIcon, 
  CheckIcon, 
  PlayCircleIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  ChartBarIcon,
  BoltIcon,
  InboxIcon,
  TagIcon,
  DocumentTextIcon,
  BellAlertIcon,
  UsersIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useAuthContext } from '../contexts/AuthContext';

const LandingPage = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { logout, isAuthenticated } = useAuthContext();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const topRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div
      ref={topRef}
      className={`landing-page min-h-screen relative ${
        isDarkMode ? 'bg-gray-950 text-white' : 'bg-white text-gray-900'
      }`}
    >
      {/* Animated Background */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-1/2 -left-1/2 w-full h-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        >
          <div className={`absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 ${isDarkMode ? 'bg-blue-500' : 'bg-blue-300'}`} />
        </motion.div>
        <motion.div
          className="absolute -bottom-1/2 -right-1/2 w-full h-full"
          animate={{ rotate: -360 }}
          transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
        >
          <div className={`absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 ${isDarkMode ? 'bg-purple-500' : 'bg-purple-300'}`} />
        </motion.div>
      </div>

      {/* Header */}
      <motion.header
        className={`fixed w-full top-0 z-50 backdrop-blur-xl border-b ${
          isDarkMode ? 'bg-gray-950/80 border-gray-800' : 'bg-white/80 border-gray-200'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <motion.div
              className="flex items-center cursor-pointer"
              onClick={() => scrollToSection(topRef)}
              whileHover={{ scale: 1.05 }}
            >
              <img src="/favicon.png" alt="FlowCXP" className="h-8 w-auto mr-2" />
              <span className={`text-xl font-bold bg-gradient-to-r ${isDarkMode ? 'from-blue-400 to-purple-400' : 'from-blue-600 to-purple-600'} bg-clip-text text-transparent`}>
                FlowCXP
              </span>
            </motion.div>

            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection(featuresRef)} className={`text-sm font-medium hover:text-blue-500 transition-colors ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Features
              </button>
              <button onClick={() => scrollToSection(pricingRef)} className={`text-sm font-medium hover:text-blue-500 transition-colors ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Pricing
              </button>
              
              <button onClick={toggleTheme} className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
                {isDarkMode ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
              </button>

              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className={`text-sm font-medium hover:text-blue-500 transition-colors ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Dashboard
                  </Link>
                  <motion.button
                    onClick={handleLogout}
                    className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium"
                    whileHover={{ scale: 1.05 }}
                  >
                    Logout
                  </motion.button>
                </>
              ) : (
                <>
                  <Link to="/login" className={`text-sm font-medium hover:text-blue-500 transition-colors ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Sign In
                  </Link>
                  <Link to="/register">
                    <motion.button
                      className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium"
                      whileHover={{ scale: 1.05 }}
                    >
                      Get Started
                    </motion.button>
                  </Link>
                </>
              )}
            </div>

            <div className="md:hidden flex items-center space-x-2">
              <button onClick={toggleTheme} className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
                {isDarkMode ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
              </button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2">
                {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                className={`md:hidden ${isDarkMode ? 'bg-gray-900' : 'bg-white'} border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                <div className="px-4 py-6 space-y-4">
                  <button onClick={() => scrollToSection(featuresRef)} className={`block w-full text-left py-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Features
                  </button>
                  <button onClick={() => scrollToSection(pricingRef)} className={`block w-full text-left py-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Pricing
                  </button>
                  {isAuthenticated ? (
                    <>
                      <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className={`block py-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Dashboard
                      </Link>
                      <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="w-full py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium">
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setMobileMenuOpen(false)} className={`block py-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Sign In
                      </Link>
                      <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                        <button className="w-full py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium">
                          Get Started
                        </button>
                      </Link>
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </motion.header>

      {/* Hero Section */}
      <motion.section className="relative z-10 pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <motion.h1
              className={`text-5xl sm:text-6xl md:text-7xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Deliver faster, smarter
              <br />
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                customer support
              </span>
              <br />
              without the chaos
            </motion.h1>

            <motion.p
              className={`text-xl sm:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              FlowCXP is a modern customer support & ticketing platform that brings all your conversations, teams, and workflows into one clean, easy-to-use workspace. Stop juggling inboxes and spreadsheets and start resolving issues in minutes, not days.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Link to="/register">
                <motion.button
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold text-lg shadow-lg flex items-center justify-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Start Your Free Trial
                </motion.button>
              </Link>
              <motion.button
                onClick={() => setShowVideoModal(true)}
                className={`px-8 py-4 rounded-lg font-semibold text-lg border-2 flex items-center justify-center ${
                  isDarkMode ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <PlayCircleIcon className="h-6 w-6 mr-2" />
                  Watch Product Demo
              </motion.button>
            </motion.div>

            <motion.p
              className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              No credit card required · Cancel anytime · Built for growing support teams
            </motion.p>
          </div>
        </div>
      </motion.section>

      {/* Problem Section */}
      <section className={`relative z-10 py-20 px-4 sm:px-6 lg:px-8 ${isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className={`text-4xl sm:text-5xl font-bold mb-6 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Your team isn't lazy.
              <br />
              <span className="text-red-500">Your tools are.</span>
            </h2>

            <p className={`text-xl mb-8 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Most companies lose customers not because they don't care, but because their support is scattered:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {[
                "Emails in one inbox, DMs in another, forms in a spreadsheet",
                "No clear ownership—tickets bounce between teams",
                "Managers have no real-time view of what's going on",
                "Customers wait too long and quietly disappear"
              ].map((problem, i) => (
                <motion.div
                  key={i}
                  className={`p-6 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    ❌ {problem}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                FlowCXP fixes this by turning messy support into a simple, trackable process.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solution Intro */}
      <section className={`relative z-10 py-20 px-4 sm:px-6 lg:px-8 ${isDarkMode ? 'bg-gray-950' : 'bg-white'}`}>
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className={`text-4xl sm:text-5xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Meet FlowCXP — your command center for customer support
            </h2>
            <p className={`text-xl leading-relaxed max-w-3xl mx-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              FlowCXP gives your team a single place to receive, track, and resolve every customer request. From the first "I need help" message to the final "Thank you!", every step is organized, visible, and measurable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={featuresRef} className={`relative z-10 py-20 px-4 sm:px-6 lg:px-8 ${isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto">
          <motion.h2
            className={`text-4xl sm:text-5xl font-bold mb-16 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            What You Can Do With FlowCXP
          </motion.h2>

          <div className="space-y-16">
            {[
              {
                icon: InboxIcon,
                title: "Never Lose a Conversation Again",
                subtitle: "All your support channels in one inbox:",
                points: [
                  "Email, social, and web requests in one queue",
                  "Simple ticket views: board, table, or list—whatever your team prefers",
                  "Clear priorities so urgent issues get handled first"
                ],
                outcome: "No more \"Did anyone reply to this?\" moments.",
                gradient: "from-blue-400 to-cyan-500"
              },
              {
                icon: UsersIcon,
                title: "Route Work to the Right People Automatically",
                subtitle: "Stop manually assigning tickets.",
                points: [
                  "Auto-assign by team (Shipping, Returns, Billing, etc.)",
                  "Balance workload across agents",
                  "Use rules and tags to send issues where they belong"
                ],
                outcome: "Less micromanagement, faster first responses.",
                gradient: "from-purple-400 to-pink-500"
              },
              {
                icon: DocumentTextIcon,
                title: "Give Agents Everything They Need in One Screen",
                subtitle: "Your team shouldn't have to dig for context.",
                points: [
                  "Rich customer profiles with history & past conversations",
                  "Timeline of previous interactions so agents see the full story",
                  "Internal notes and memos to keep everyone aligned"
                ],
                outcome: "Shorter replies, fewer back-and-forth messages.",
                gradient: "from-green-400 to-emerald-500"
              },
              {
                icon: ClockIcon,
                title: "Keep Promises With SLAs and Escalations",
                subtitle: "Set clear expectations—and hit them.",
                points: [
                  "Custom priority levels (Low → Urgent)",
                  "SLA timers and alerts when tickets are at risk",
                  "Automatic escalations when issues sit too long"
                ],
                outcome: "Reliable response times your customers can trust.",
                gradient: "from-yellow-400 to-orange-500"
              },
              {
                icon: ChartBarIcon,
                title: "See How Your Support Is Really Performing",
                subtitle: "Know what's working and what's not.",
                points: [
                  "Dashboards for ticket volume, response times, and resolutions",
                  "Team and agent performance views",
                  "Channel-level insights: which sources create the most work"
                ],
                outcome: "Make decisions based on data, not gut feeling.",
                gradient: "from-red-400 to-pink-500"
              },
              {
                icon: CogIcon,
                title: "Scale With a Real Organization Structure",
                subtitle: "FlowCXP grows with your company, not against it.",
                points: [
                  "Departments, teams, and groups based on how you work",
                  "Managers and reporting lines built into the platform",
                  "Permissions for admins, agents, and requesters"
                ],
                outcome: "A support system that feels like your org chart, not a cluttered inbox.",
                gradient: "from-indigo-400 to-purple-500"
              },
              {
                icon: BoltIcon,
                title: "Delight Customers With Fast, Consistent Replies",
                subtitle: "Automate the boring parts, personalize what matters.",
                points: [
                  "Saved replies for common questions",
                  "Smart bot rules for instant answers to repetitive queries",
                  "Notifications to keep customers updated on progress"
                ],
                outcome: "Customers feel seen, heard, and helped—quickly.",
                gradient: "from-teal-400 to-cyan-500"
              }
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  className={`p-8 rounded-3xl border ${
                    isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
                  }`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="flex items-start gap-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${benefit.gradient} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {benefit.title}
                      </h3>
                      <p className={`text-lg mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {benefit.subtitle}
                      </p>
                      <ul className="space-y-2 mb-4">
                        {benefit.points.map((point, i) => (
                          <li key={i} className={`flex items-start ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            {point}
                          </li>
                        ))}
                      </ul>
                      <p className={`font-semibold text-lg ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        ✨ Outcome: {benefit.outcome}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Omnichannel Section */}
      <section className={`relative z-10 py-20 px-4 sm:px-6 lg:px-8 ${isDarkMode ? 'bg-gray-950' : 'bg-white'}`}>
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className={`text-4xl sm:text-5xl font-bold mb-6 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              One platform for every "Can you help me?"
            </h2>
            <p className={`text-xl mb-12 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Whether customers reach out by email, your website, or connected messaging channels, FlowCXP turns every message into a trackable ticket—with full history, attachments, and ownership.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: ChatBubbleLeftRightIcon, title: "Unified view of all conversations" },
                { icon: TagIcon, title: "Attach screenshots, invoices, or photos to any ticket" },
                { icon: BellAlertIcon, title: "Real-time updates as messages are sent and received" }
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={i}
                    className={`p-6 rounded-xl border text-center ${
                      isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-200'
                    }`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                  >
                    <Icon className={`h-12 w-12 mx-auto mb-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    <p className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {item.title}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof */}
      <section className={`relative z-10 py-20 px-4 sm:px-6 lg:px-8 ${isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className={`text-4xl sm:text-5xl font-bold mb-12 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Built for teams that can't afford to drop the ball
            </h2>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {[
                "E-commerce and online retailers handling order, shipping, and return questions",
                "SaaS and product companies managing bug reports, onboarding, and support",
                "Financial services and digital products where response time really matters"
              ].map((useCase, i) => (
                <motion.div
                  key={i}
                  className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{useCase}</p>
                </motion.div>
              ))}
            </div>

            <div className={`p-8 rounded-2xl border ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
              <p className={`text-xl italic mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                "FlowCXP turned our scattered inboxes into a single, calm workspace. Our customers get answers faster, and our team finally feels in control."
              </p>
              <p className={`font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                — Support Lead
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className={`relative z-10 py-20 px-4 sm:px-6 lg:px-8 ${isDarkMode ? 'bg-gray-950' : 'bg-white'}`}>
        <div className="max-w-5xl mx-auto">
          <motion.h2
            className={`text-4xl sm:text-5xl font-bold mb-12 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            From sign-up to first resolved ticket in under a day
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              { step: "1", title: "Create your workspace", desc: "Set up your organization, departments, and support groups." },
              { step: "2", title: "Connect your channels", desc: "Plug in email and other messaging channels." },
              { step: "3", title: "Invite your team", desc: "Add agents, define roles, and set up your basic workflows." },
              { step: "4", title: "Start resolving tickets", desc: "Track, prioritize, and respond to every request—no migration headache required." }
            ].map((item, i) => (
              <motion.div
                key={i}
                className={`p-6 rounded-xl border ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {item.title}
                    </h3>
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section ref={pricingRef} className={`relative z-10 py-20 px-4 sm:px-6 lg:px-8 ${isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className={`text-4xl sm:text-5xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Simple pricing that scales with you
            </h2>
            <p className={`text-xl mb-12 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Start small, add agents as you grow. No setup fees, no long-term lock-in—just modern customer support at a fair price.
            </p>

            <Link to="/register">
              <motion.button
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold text-lg shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                👉 View Pricing & Get Started
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className={`relative z-10 py-20 px-4 sm:px-6 lg:px-8 ${isDarkMode ? 'bg-gray-950' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto">
          <motion.h2
            className={`text-4xl sm:text-5xl font-bold mb-12 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Questions? We've built FlowCXP for teams like yours
          </motion.h2>

          <div className="space-y-6">
            {[
              {
                q: "Is FlowCXP only for large companies?",
                a: "No. FlowCXP works great for small teams and scales up as you hire more agents."
              },
              {
                q: "How long does it take to get started?",
                a: "Most teams are up and running the same day—often within a couple of hours."
              },
              {
                q: "Do I need a dedicated IT team?",
                a: "No. If you can use modern web tools, you can set up FlowCXP. We handle the infrastructure behind the scenes."
              }
            ].map((faq, i) => (
              <motion.div
                key={i}
                className={`p-6 rounded-xl border ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-200'}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {faq.q}
                </h3>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                  {faq.a}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={`relative z-10 py-20 px-4 sm:px-6 lg:px-8 ${isDarkMode ? 'bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900' : 'bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600'}`}>
        <motion.div
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Turn support from a cost center into a customer advantage
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Your customers are already talking. The question is whether your team can keep up—with clarity, speed, and confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Link to="/register">
              <motion.button
                className="px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold text-lg shadow-xl"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🚀 Start Your Free Trial
              </motion.button>
            </Link>
            <motion.button
              onClick={() => setShowVideoModal(true)}
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-lg font-semibold text-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              📺 Watch a 3-Minute Demo
            </motion.button>
          </div>
          <p className="text-sm text-blue-200">
            No credit card · Cancel anytime · Support included
          </p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className={`relative z-10 py-8 px-4 sm:px-6 lg:px-8 border-t ${isDarkMode ? 'bg-gray-950 border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="max-w-7xl mx-auto">
          <div className={`flex flex-col sm:flex-row justify-between items-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <div className="mb-4 sm:mb-0">
              &copy; {new Date().getFullYear()} FlowCXP. All rights reserved.
            </div>
            <div className="flex space-x-6">
              <Link to="/privacy-policy" className="hover:text-blue-500 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="hover:text-blue-500 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowVideoModal(false)}
          >
            <motion.div
              className="relative w-full max-w-4xl bg-black rounded-2xl overflow-hidden"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowVideoModal(false)}
                className="absolute top-4 right-4 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
              <div className="relative w-full aspect-video bg-black">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/Iom4ncw-zpo?autoplay=1&rel=0"
                  title="FlowCXP 3-Minute Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Back to Top */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            className={`fixed bottom-8 right-8 p-3 rounded-full shadow-lg z-50 ${
              isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
            } text-white`}
            onClick={() => topRef.current?.scrollIntoView({ behavior: 'smooth' })}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ArrowUpIcon className="h-6 w-6" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
